import { isEmpty } from 'lodash-es';
import { makes } from 'client/data/constants/active-makes';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { PageModel } from 'client/data/models/page';
import {
  VehicleModel,
  buildEstimatedStyleIdsPath,
  buildModelsPath,
  buildVehicleStylePath,
} from 'client/data/models/vehicle-v2';
import { getQuery } from 'client/utils/location';
import { getAppraiserStylesPath, isAllStylesPreloadEligible } from 'client/data/utils/appraiser-preload-experiment';
import { getLoadablePage } from 'site-modules/shared/components/loadable-page/loadable-page';
import { makeNiceName as getNiceName } from 'site-modules/shared/utils/nice-name';
import { transformStylesToStyleIds, getSelectedStyleId } from 'site-modules/appraiser/pages/appraiser/appraiser-utils';
import {
  APPRAISER_STYLE_HIGHLIGHTS_PAGE_NAME,
  APPRAISER_STYLE_PAGE_NAME,
} from 'site-modules/appraiser/constants/appraiser';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';

const preloadStyleByMMY = (
  preloader,
  { estimatedStyleIdsPath, stylesPath, queryStyleIds, shouldPreloadAllStyles = true, isGraphqlStylesPath }
) => {
  preloader.resolve(estimatedStyleIdsPath, VehicleModel);
  preloader.resolve('estimatedAppraisalVin', VehicleModel);

  if (shouldPreloadAllStyles || isGraphqlStylesPath) {
    preloader.resolve(stylesPath, VehicleModel);
  }

  return preloader.load().then(modelState => {
    const estimatedStyleIds = modelState.get(estimatedStyleIdsPath, VehicleModel);
    const allStylesForVehicle = modelState.get(stylesPath, VehicleModel);
    const allStyleIdsForVehicle = Array.isArray(allStylesForVehicle)
      ? transformStylesToStyleIds(allStylesForVehicle)
      : [];

    const selectedStyleId = getSelectedStyleId(estimatedStyleIds, queryStyleIds, allStyleIdsForVehicle);
    const selectedStylePath = buildVehicleStylePath({ styleId: selectedStyleId });
    const selectedGraphqlStyle =
      isGraphqlStylesPath && allStylesForVehicle?.find(style => String(style.id) === String(selectedStyleId));

    if (selectedGraphqlStyle && isEmpty(modelState.get(selectedStylePath, VehicleModel))) {
      const { makeSlug, modelSlug, submodels } = selectedGraphqlStyle;
      // Preserve the references used by vehicle dropdowns, VIN validation and truck detection.
      preloader.resolve(buildModelsPath({ make: makeSlug }), VehicleModel);
      preloader.set(selectedStylePath, VehicleModel, {
        ...selectedGraphqlStyle,
        // TODO: Move vehicle style reference construction into vehicle-v2.js
        make: { $ref: `#/makes/${makeSlug}` },
        model: { $ref: `#/makes/${makeSlug}/models/${modelSlug}` },
        submodels: { $ref: `#/makes/${makeSlug}/models/${modelSlug}/submodels/${submodels.slug}` },
      });
    } else {
      preloader.resolve(selectedStylePath, VehicleModel);
    }
    return preloader.load();
  });
};

const AppraiserPreload = async (params, store, pageName) => {
  const { make, model, year } = params;
  const state = store.getState();

  const location = state.pageContext?.location;
  const { styleIds: queryStyleIds } = getQuery(location);

  const preloader = new ModelPreloader(store);
  preloader.set('page.name', PageModel, pageName);

  const emo4182Recipe = ExperimentUtil.getForcedOrAssignedRecipeName({
    state: store.getState(),
    campaignName: 'EMO-4182',
    defaultVal: 'ctrl',
  });

  const estimatedStyleIdsPath = buildEstimatedStyleIdsPath(params);
  const stylesPath = getAppraiserStylesPath({
    pageName,
    location,
    emo4182Recipe,
    make: getNiceName(make),
    model: getNiceName(model),
    year,
  });
  const isGraphqlStylesPath = stylesPath.endsWith('.stylesBySubmodel');

  const isSingleStyleIdInQuery = !isAllStylesPreloadEligible(queryStyleIds);

  // When exactly one style ID is in the query, try it directly first.
  const singleQueryStyleId = isSingleStyleIdInQuery ? queryStyleIds : null;

  if (singleQueryStyleId) {
    preloader.resolve(buildVehicleStylePath({ styleId: singleQueryStyleId }), VehicleModel);

    return preloader.load().then(modelState => {
      const queryStyle = modelState.get(buildVehicleStylePath({ styleId: singleQueryStyleId }), VehicleModel);

      // Accept the style only when it is non-empty and its MMY matches the page params.
      const isValidStyle =
        !isEmpty(queryStyle) &&
        queryStyle.makeSlug === make &&
        queryStyle.modelSlug === model &&
        String(queryStyle.year) === String(year);

      if (isValidStyle) {
        return modelState;
      }

      // Style was empty or MMY mismatch — fall back to full preload to resolve the correct style.
      return preloadStyleByMMY(preloader, {
        estimatedStyleIdsPath,
        stylesPath,
        queryStyleIds,
        isGraphqlStylesPath,
      });
    });
  }

  return preloadStyleByMMY(preloader, {
    estimatedStyleIdsPath,
    stylesPath,
    queryStyleIds,
    shouldPreloadAllStyles: false,
    isGraphqlStylesPath,
  });
};

const shouldImportWithVinLpTabs = ({ store }) => {
  const state = store.getState();
  const location = state.pageContext?.location;
  const { vin } = getQuery(location);
  return !vin;
};

const routes = [
  {
    path: `/:make(${makes.join('|')})/:model/:year(\\d{4})/appraisal-value`,
    exact: true,
    preload: (params, store) => AppraiserPreload(params, store, APPRAISER_STYLE_PAGE_NAME),
    page: getLoadablePage((params, store) => {
      if (shouldImportWithVinLpTabs({ params, store })) {
        return import(
          /* webpackChunkName: "appraiser-with-vin-lp-tabs" */ '../pages/appraiser/appraiser-definition-with-vin-lp-tabs'
        );
      }

      return import(/* webpackChunkName: "appraiser" */ '../pages/appraiser/appraiser-definition');
    }),
    chunkName: 'appraiser',
    resolveLocationInParallelToPagePreload: true,
  },
  {
    path: `/:make(${makes.join('|')})/:model/:year(\\d{4})/appraisal-value/index.app`,
    exact: true,
    preload: (params, store) => AppraiserPreload(params, store, APPRAISER_STYLE_PAGE_NAME),
    page: getLoadablePage((params, store) => {
      if (shouldImportWithVinLpTabs({ params, store })) {
        return import(
          /* webpackChunkName: "appraiser-with-vin-lp-tabs-app" */ '../pages/appraiser/appraiser-definition-with-vin-lp-tabs-app'
        );
      }

      return import(/* webpackChunkName: "appraiser-app" */ '../pages/appraiser/appraiser-definition-app');
    }),
    chunkName: 'appraiser-app',
    resolveLocationInParallelToPagePreload: true,
  },
  {
    path: `/:make(${makes.join('|')})/:model/:year(\\d{4})/appraisal-value/highlights`,
    exact: true,
    preload: (params, store) => AppraiserPreload(params, store, APPRAISER_STYLE_HIGHLIGHTS_PAGE_NAME),
    page: getLoadablePage((params, store) => {
      if (shouldImportWithVinLpTabs({ params, store })) {
        return import(
          /* webpackChunkName: "appraiser-highlights-with-vin-lp-tabs" */ '../pages/appraiser/appraiser-highlights-definition-with-vin-lp-tabs'
        );
      }

      return import(
        /* webpackChunkName: "appraiser-highlights" */ '../pages/appraiser/appraiser-highlights-definition'
      );
    }),
    chunkName: 'appraiser-highlights',
    resolveLocationInParallelToPagePreload: true,
  },
];

export { routes };
